/**
 * Early Access popup
 *
 * Flow:
 * 1. User clicks submit (button is type="button" — form never submits natively)
 * 2. Validate email format
 * 3. Klaviyo list check (Theme settings → Klaviyo list ID)
 *    → duplicate (HTTP 400 or already_subscribed flags): show error, stop (no hCaptcha)
 *    → unavailable (network/other error): show error, stop
 * 4. New email: Shopify.captcha.protect() → mark pending in sessionStorage → form.submit()
 * 5. Password store redirects to /password#contact_form (drops customer_posted params)
 * 6. On reload: pending + #contact_form → replace the form with the success message
 *
 * Do NOT add fetch()/iframe checks against Shopify — they trigger /challenge or invalid parameters.
 * Do NOT set data-nocaptcha on the form — it breaks captcha submission.
 */
// @ts-check

(() => {
  const rootElement = document.querySelector('[data-early-access]');
  if (!(rootElement instanceof HTMLElement)) return;

  /** @type {HTMLElement} */
  const root = rootElement;

  const PENDING_KEY = 'chaser_early_access_pending';
  const PENDING_MAX_AGE_MS = 5 * 60 * 1000;

  const siteLocked = root.dataset.siteLocked !== 'false';
  const duplicateMessage =
    root.dataset.duplicateMessage || 'This email is already registered for Early Access.';
  const successMessage =
    root.dataset.successMessage ||
    "You're on the list. We'll email your discount code before launch.";
  const invalidEmailMessage =
    root.dataset.invalidEmailMessage || 'Please enter a valid email address.';
  const unavailableMessage =
    root.dataset.unavailableMessage ||
    'Registration is temporarily unavailable. Please try again later.';
  const loadingLabel = root.dataset.loadingLabel || 'Registering…';
  const klaviyoListId = (root.dataset.klaviyoListId || 'Y7XSDp').trim();
  const countdownRoot = root.querySelector('[data-early-access-countdown]');
  const backdrop = root.querySelector('[data-early-access-backdrop]');
  const formWrap = root.querySelector('[data-early-access-form-wrap]');
  const form = root.querySelector('form.early-access__form');
  const emailInput = root.querySelector('#EarlyAccessEmail');
  const submitButton = root.querySelector('[data-early-access-submit]');
  const errorEl = root.querySelector('[data-early-access-error]');
  const successPanel = root.querySelector('[data-early-access-success]');
  const successTextEl = root.querySelector('.early-access__success-text');

  const LOCK_SELECTORS = ['#header-group', '#MainContent'];

  let isSubmitting = false;

  /**
   * @param {string} value
   */
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  /**
   * @param {string} email
   */
  const markPendingRegistration = (email) => {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({ email, at: Date.now() }));
    } catch (_) {
      // Ignore storage failures.
    }
  };

  /**
   * @returns {{ email: string, at: number } | null}
   */
  const readPendingRegistration = () => {
    try {
      const raw = sessionStorage.getItem(PENDING_KEY);
      if (!raw) return null;

      const data = JSON.parse(raw);
      if (!data || typeof data.at !== 'number') return null;
      if (Date.now() - data.at > PENDING_MAX_AGE_MS) {
        sessionStorage.removeItem(PENDING_KEY);
        return null;
      }

      return {
        email: typeof data.email === 'string' ? data.email : '',
        at: data.at,
      };
    } catch (_) {
      return null;
    }
  };

  const clearPendingRegistration = () => {
    try {
      sessionStorage.removeItem(PENDING_KEY);
    } catch (_) {
      // Ignore storage failures.
    }
  };

  const isContactFormReturn = () => window.location.hash === '#contact_form';

  const isDuplicateSubmissionUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get('form_type') === 'customer' &&
      params.get('customer_posted') !== 'true' &&
      params.get('contact_posted') !== 'true'
    );
  };

  const isSuccessfulSubmissionUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('customer_posted') === 'true' || params.get('contact_posted') === 'true';
  };

  const getEmailFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return (params.get('contact[email]') || '').trim().toLowerCase();
  };

  const cleanFormReturnUrl = () => {
    const url = new URL(window.location.href);
    const keys = ['form_type', 'utf8', 'contact[email]', 'customer_posted', 'contact_posted'];
    let changed = false;

    for (const key of keys) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }

    if (url.hash === '#contact_form') {
      url.hash = '';
      changed = true;
    }

    if (changed) {
      history.replaceState(null, '', `${url.pathname}${url.search}`);
    }
  };

  /**
   * @param {string} message
   */
  const showError = (message) => {
    if (!(errorEl instanceof HTMLElement)) return;

    errorEl.textContent = message;
    errorEl.hidden = false;
    errorEl.removeAttribute('hidden');

    if (emailInput instanceof HTMLInputElement) {
      emailInput.classList.add('early-access__input--invalid');
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.setAttribute('aria-describedby', 'EarlyAccessFormError');
      emailInput.focus();
    }
  };

  const clearError = () => {
    if (!(errorEl instanceof HTMLElement)) return;

    errorEl.textContent = '';
    errorEl.hidden = true;

    if (emailInput instanceof HTMLInputElement) {
      emailInput.classList.remove('early-access__input--invalid');
      emailInput.removeAttribute('aria-invalid');
      emailInput.removeAttribute('aria-describedby');
    }
  };

  const showSuccess = () => {
    clearError();

    if (formWrap instanceof HTMLElement) {
      formWrap.hidden = true;
    }

    if (successTextEl instanceof HTMLElement) {
      successTextEl.textContent = successMessage;
    }

    if (successPanel instanceof HTMLElement) {
      successPanel.hidden = false;
      successPanel.removeAttribute('hidden');
    }
  };

  /**
   * @param {boolean} isLoading
   */
  const setLoading = (isLoading) => {
    if (!(submitButton instanceof HTMLButtonElement)) return;

    if (isLoading) {
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.setAttribute('aria-label', loadingLabel);
      submitButton.disabled = true;
      return;
    }

    submitButton.removeAttribute('aria-busy');
    submitButton.disabled = false;
  };

  const applyStoreLock = () => {
    document.documentElement.classList.add('early-access-site-lock');

    for (const selector of LOCK_SELECTORS) {
      const element = document.querySelector(selector);
      if (element instanceof HTMLElement) {
        element.setAttribute('aria-hidden', 'true');
      }
    }
  };

  /**
   * @param {number} endTimestampSeconds
   */
  const startCountdown = (endTimestampSeconds) => {
    if (!(countdownRoot instanceof HTMLElement)) return;

    const daysEl = countdownRoot.querySelector('[data-countdown-days]');
    const hoursEl = countdownRoot.querySelector('[data-countdown-hours]');
    const minutesEl = countdownRoot.querySelector('[data-countdown-minutes]');
    const secondsEl = countdownRoot.querySelector('[data-countdown-seconds]');

    if (
      !(daysEl instanceof HTMLElement) ||
      !(hoursEl instanceof HTMLElement) ||
      !(minutesEl instanceof HTMLElement) ||
      !(secondsEl instanceof HTMLElement)
    ) {
      return;
    }

    const endMs = endTimestampSeconds * 1000;

    const render = () => {
      const remainingMs = Math.max(0, endMs - Date.now());
      const totalSeconds = Math.floor(remainingMs / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      daysEl.textContent = String(days).padStart(2, '0');
      hoursEl.textContent = String(hours).padStart(2, '0');
      minutesEl.textContent = String(minutes).padStart(2, '0');
      secondsEl.textContent = String(seconds).padStart(2, '0');

      if (remainingMs <= 0) {
        countdownRoot.hidden = true;
        window.clearInterval(timerId);
      }
    };

    render();
    const timerId = window.setInterval(render, 1000);
  };

  /**
   * @param {unknown} value
   */
  const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

  /** @typedef {'duplicate' | 'available' | 'unavailable'} EmailRegistrationStatus */

  /**
   * Probe Klaviyo list membership via the legacy subscribe endpoint.
   * HTTP 400 = email already on the list; 200 = not yet subscribed.
   *
   * @param {string} email
   * @returns {Promise<EmailRegistrationStatus>}
   */
  const checkEmailRegistration = async (email) => {
    try {
      const response = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ g: klaviyoListId, email }),
      });

      if (response.status === 400) {
        return 'duplicate';
      }

      if (!response.ok) {
        return 'unavailable';
      }

      const json = await response.json();
      if (!json?.success) {
        return 'unavailable';
      }

      const data = json.data || {};
      if (
        isTruthyFlag(data.is_subscribed) ||
        isTruthyFlag(data.already_subscribed) ||
        isTruthyFlag(data.is_already_subscribed)
      ) {
        return 'duplicate';
      }

      return 'available';
    } catch (_) {
      return 'unavailable';
    }
  };

  /**
   * @param {HTMLFormElement} customerForm
   * @param {string} email
   */
  const submitNewRegistration = (customerForm, email) => {
    customerForm.dataset.earlyAccessAllowSubmit = 'true';

    const shopify = /** @type {{ captcha?: { protect?: (form: HTMLFormElement, cb: () => void) => void } } | undefined} */ (
      window.Shopify
    );

    const commitSubmit = () => {
      // Password stores redirect to /password#contact_form and drop customer_posted.
      // Mark pending only right before the real submit so we can show success on return.
      markPendingRegistration(email);
      customerForm.submit();
    };

    if (shopify?.captcha?.protect) {
      shopify.captcha.protect(customerForm, commitSubmit);
      return;
    }

    commitSubmit();
  };

  /**
   * @returns {Promise<void>}
   */
  const handleRegistrationAttempt = async () => {
    if (!(form instanceof HTMLFormElement) || !(emailInput instanceof HTMLInputElement)) return;
    if (isSubmitting) return;

    const email = emailInput.value.trim().toLowerCase();
    if (!email) return;

    clearError();

    if (!isValidEmail(email)) {
      showError(invalidEmailMessage);
      return;
    }

    if (!klaviyoListId) {
      showError(unavailableMessage);
      return;
    }

    isSubmitting = true;
    setLoading(true);

    try {
      const registrationStatus = await checkEmailRegistration(email);

      if (registrationStatus === 'duplicate') {
        showError(duplicateMessage);
        return;
      }

      if (registrationStatus === 'unavailable') {
        showError(unavailableMessage);
        return;
      }

      submitNewRegistration(form, email);
    } catch (_) {
      showError(unavailableMessage);
    } finally {
      isSubmitting = false;
      setLoading(false);
    }
  };

  if (siteLocked) applyStoreLock();

  backdrop?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
  });

  const countdownEnd = Number(root.dataset.countdownEnd || '0');
  if (countdownEnd > 0) startCountdown(countdownEnd);

  const serverSuccessfulSubmission = root.dataset.successfulSubmission === 'true';
  const serverDuplicateSubmission = root.dataset.duplicateSubmission === 'true';
  const serverSubmittedEmail = (root.dataset.submittedEmail || '').trim().toLowerCase();
  const pendingRegistration = readPendingRegistration();
  const returnedFromContactForm = isContactFormReturn();
  // Password redirects often drop query params; treat a fresh pending submit as success too.
  const pendingLooksFresh =
    Boolean(pendingRegistration) && Date.now() - pendingRegistration.at < 90 * 1000;

  if (
    serverSuccessfulSubmission ||
    isSuccessfulSubmissionUrl() ||
    (pendingRegistration && (returnedFromContactForm || pendingLooksFresh))
  ) {
    clearPendingRegistration();
    showSuccess();
    cleanFormReturnUrl();
  } else if (serverDuplicateSubmission) {
    clearPendingRegistration();
    if (serverSubmittedEmail && emailInput instanceof HTMLInputElement) {
      emailInput.value = serverSubmittedEmail;
    }
    showError(duplicateMessage);
    cleanFormReturnUrl();
  } else if (isDuplicateSubmissionUrl()) {
    clearPendingRegistration();
    const urlEmail = getEmailFromUrl();
    if (urlEmail && emailInput instanceof HTMLInputElement) {
      emailInput.value = urlEmail;
    }
    showError(duplicateMessage);
    cleanFormReturnUrl();
  } else if (returnedFromContactForm) {
    // Stale #contact_form hash with no pending signup — just clean the URL.
    cleanFormReturnUrl();
  }

  if (form instanceof HTMLFormElement) {
    form.addEventListener(
      'submit',
      (event) => {
        if (form.dataset.earlyAccessAllowSubmit !== 'true') {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
      },
      true
    );
  }

  submitButton?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleRegistrationAttempt();
  });

  emailInput?.addEventListener('input', clearError);

  emailInput?.addEventListener('keydown', (event) => {
    if (!(event instanceof KeyboardEvent) || event.key !== 'Enter') return;
    event.preventDefault();
    event.stopPropagation();
    handleRegistrationAttempt();
  });
})();
